import { getAppInsightsConfig } from "@/constants/monitoring/appInsightsConfig";
import { ApplicationInsights, ITelemetryItem, SeverityLevel } from "@microsoft/applicationinsights-web";

let appInsights: ApplicationInsights | undefined;

// Scrubs auth tokens/keys from auto-tracked URLs. See docs/application-insights-rum.md.
const stripQueryAndFragment = (url: string): string => {
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return url.split(/[?#]/)[0];
  }
};

const scrubUrlFields = (item: ITelemetryItem) => {
  const baseData = item.baseData as Record<string, unknown> | undefined;
  if (!baseData) return;

  (["uri", "url", "refUri", "data"] as const).forEach((field) => {
    const value = baseData[field];
    if (typeof value === "string") {
      baseData[field] = stripQueryAndFragment(value);
    }
  });
};

// See docs/application-insights-rum.md. No-op (no SDK instance, no network calls, no console
// output) whenever getAppInsightsConfig() is undefined.
export const initAppInsights = (): ApplicationInsights | undefined => {
  if (appInsights) return appInsights;

  const config = getAppInsightsConfig();
  if (!config) return undefined;

  appInsights = new ApplicationInsights({ config });
  appInsights.loadAppInsights();
  appInsights.addTelemetryInitializer(scrubUrlFields);
  appInsights.trackPageView(); // initial load; enableAutoRouteTracking covers subsequent SPA nav

  // Flushes on unload everywhere, including the recording page: usePreventExitPage.ts's
  // beforeunload flush only fires while shouldBlock() is true (actively recording), so a
  // reload while merely paused would otherwise have no flush trigger at all. See
  // "Unload-time delivery" in docs/application-insights-rum.md.
  window.addEventListener("pagehide", () => {
    flushAppInsights();
  });

  return appInsights;
};

// Callers (error boundaries, root-level error hooks) must never see telemetry itself throw.
export const trackException = (error: unknown, properties?: Record<string, string>): void => {
  if (!appInsights) return;

  try {
    const exception = error instanceof Error ? error : new Error(String(error));
    appInsights.trackException({ exception, severityLevel: SeverityLevel.Error, properties });
  } catch (err) {
    console.error("Failed to track exception:", err);
  }
};

export const trackAppEvent = (name: string, properties?: Record<string, string>): void => {
  if (!appInsights) return;

  try {
    appInsights.trackEvent({ name, properties });
  } catch (err) {
    console.error("Failed to track event:", err);
  }
};

export const trackAppTrace = (message: string, properties?: Record<string, string>): void => {
  if (!appInsights) return;

  try {
    appInsights.trackTrace({ message, severityLevel: SeverityLevel.Information }, properties);
  } catch (err) {
    console.error("Failed to track trace:", err);
  }
};

// Forces queued telemetry out immediately. See "Unload-time delivery" in
// docs/application-insights-rum.md.
export const flushAppInsights = (): void => {
  if (!appInsights) return;

  try {
    appInsights.flush(false);
  } catch (err) {
    console.error("Failed to flush telemetry:", err);
  }
};
